import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="export-range-setter"
// Spec D2: a Mesiac / Štvrťrok / Rok / Vlastné segment plus a ‹ period › stepper.
// The start/end date inputs stay in the form (the controllers read them); they
// are only SHOWN for "custom".
//
// "all" (the bank-statement export's Všetko) is the one range with no dates: both
// inputs are emptied, which the server reads as "no period filter". The inputs are
// `required`, so they must stop being required for it — an empty required control
// inside the hidden custom panel cannot be focused, and the browser then refuses
// the submit without a word.
export default class extends Controller {
  static targets = ["startDate", "endDate", "dateRange", "label", "sublabel", "custom", "segment", "stepper"]
  static values = { locale: String }

  connect() {
    this.apply(this.dateRangeTarget.value || "month")
  }

  select(event) {
    this.apply(event.currentTarget.dataset.range)
  }

  previous() {
    this.step(-1)
  }

  next() {
    this.step(1)
  }

  apply(range) {
    this.dateRangeTarget.value = range
    for (const button of this.segmentTargets) {
      button.setAttribute("aria-pressed", String(button.dataset.range === range))
    }
    const custom = range === "custom"
    const undated = range === "all"
    if (this.hasCustomTarget) this.customTarget.hidden = !custom
    if (this.hasStepperTarget) this.stepperTarget.hidden = custom || undated
    this.startDateTarget.required = !undated
    this.endDateTarget.required = !undated
    if (!custom) this.write(this.getDateRange(range, new Date().getFullYear()))
    this.renderLabel()
  }

  step(direction) {
    const range = this.dateRangeTarget.value
    if (range === "custom" || !this.startDateTarget.value) return

    const start = new Date(`${this.startDateTarget.value}T00:00:00`)
    if (range === "year") this.write(this.getYearRange(start.getFullYear() + direction))
    if (range === "month") this.write(this.getMonthRange(start.getFullYear(), start.getMonth() + 1 + direction))
    if (range === "quarter") this.write(this.getQuarterRange(start.getFullYear(), start.getMonth() + 3 * direction))
    this.renderLabel()
  }

  write({ startDate, endDate }) {
    this.startDateTarget.value = startDate
    this.endDateTarget.value = endDate
  }

  renderLabel() {
    if (!this.hasLabelTarget || !this.startDateTarget.value) return

    const start = new Date(`${this.startDateTarget.value}T00:00:00`)
    const end = new Date(`${this.endDateTarget.value}T00:00:00`)
    const range = this.dateRangeTarget.value
    const locale = this.localeValue || document.documentElement.lang || "sk"
    const short = new Intl.DateTimeFormat(locale, { day: "numeric", month: "numeric", year: "numeric" })

    if (range === "month") {
      this.labelTarget.textContent = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(start)
    } else if (range === "quarter") {
      this.labelTarget.textContent = `Q${Math.floor(start.getMonth() / 3) + 1} ${start.getFullYear()}`
    } else if (range === "year") {
      this.labelTarget.textContent = String(start.getFullYear())
    } else {
      this.labelTarget.textContent = ""
    }

    if (this.hasSublabelTarget) this.sublabelTarget.textContent = `${short.format(start)} – ${short.format(end)}`
  }

  getDateRange(range, year) {
    switch (range) {
      case "year":
        return this.getYearRange(year)
      case "month":
        return this.getMonthRange(year)
      case "quarter":
        return this.getQuarterRange(year)
      default:
        return { startDate: null, endDate: null }
    }
  }

  getYearRange(year) {
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`
    }
  }

  getMonthRange(year, month = null) {
    // When month is not provided, default to previous month
    if (month === null) {
      const currentMonth = new Date().getMonth() // 0-indexed (0=Jan, 11=Dec)
      if (currentMonth === 0) {
        // January - previous month is December of previous year
        month = 12
        year -= 1
      } else {
        // Previous month (currentMonth is 0-indexed, so it equals previous month in 1-indexed)
        month = currentMonth
      }
    }

    // Handle month overflow/underflow from stepper navigation
    if (month <= 0) {
      month = 12
      year -= 1
    } else if (month > 12) {
      month = 1
      year += 1
    }

    const startDate = `${year}-${this.padDate(month)}-01`
    const endDate = `${year}-${this.padDate(month)}-${this.getLastDayOfMonth(year, month)}`
    return {
      startDate: startDate,
      endDate: endDate
    }
  }

  // Helper method to get the last day of the month
  getLastDayOfMonth(year, month) {
    return new Date(year, month, 0).getDate() // Get the last day of the month
  }

  // Helper method to pad date with leading zero
  padDate(date) {
    return String(date).padStart(2, "0") // Pad with zero if needed
  }

  getQuarterRange(year, month = null) {
    let currentMonth = month !== null ? month : new Date().getMonth() // 0-11
    if (currentMonth < 0) {
      currentMonth = 9
      year -= 1
    }
    if (currentMonth > 11) {
      currentMonth = 0
      year += 1
    }
    const currentQuarter = Math.floor((currentMonth + 3) / 3) // Calculate quarter (1-4)
    switch (currentQuarter) {
      case 1:
        return {
          startDate: `${year}-01-01`,
          endDate: `${year}-03-31`
        }
      case 2:
        return {
          startDate: `${year}-04-01`,
          endDate: `${year}-06-30`
        }
      case 3:
        return {
          startDate: `${year}-07-01`,
          endDate: `${year}-09-30`
        }
      case 4:
        return {
          startDate: `${year}-10-01`,
          endDate: `${year}-12-31`
        }
      default:
        return { startDate: null, endDate: null }
    }
  }
}
